import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { BaseContainer } from '@base/base/BaseContainer'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import GameSignals from '../../signals/GameSignals'
import Facade from '../../Facade'
import gsap from 'gsap'
import { ContainersNames } from '@/containers/ContainersNames'
import {
    applyReelFrameBaseLayout,
    applyReelFrameFeatureLayout,
    applyReelFrameTexture,
    scheduleReelFrameLayoutSync,
    startReelFrameAnimation,
    stopReelFrameAnimation,
    REEL_FRAME_ANIMATION_MAIN,
} from '@/managers/ui/ReelManager'

const isHoldAndWinFrameActive = (data?: ICustomEventData): boolean => {
    const introKind = String((data as any)?.signalData?.kind || '').toLowerCase()
    if (introKind) {
        return introKind === 'holdandwin'
    }

    if (Facade.model?.spinState?.isHoldAndWinActive) {
        return true
    }

    const spinType = String(Facade.model?.spinData?.spin_type || '').toLowerCase()
    const nextRoundType = String(Facade.model?.spinData?.next_round_type || '').toLowerCase()
    return spinType === 'respin' || nextRoundType === 'respins' || nextRoundType === 'respin'
}

const syncReelFrameTextureAndLayout = (data: ICustomEventData, options?: { schedule?: boolean }): void => {
    const element = data.component.componentElement as BaseContainer
    const holdAndWinActive = isHoldAndWinFrameActive(data)

    applyReelFrameTexture(element, holdAndWinActive ? 'reel_hold-win.png' : 'reel_main.png')

    if (options?.schedule) {
        scheduleReelFrameLayoutSync(element)
        return
    }

    applyReelFrameFeatureLayout(element)
}

export const REEL_COMPONENT: IComponent = {
    name: ContainersNames.REEL_CONTAINER,
    type: AssetType.CONTAINER,
    position: [
        {
            orientation: Orientation.LANDSCAPE,
            x: 0,
            y: 0,
        },
        {
            orientation: Orientation.PORTRAIT,
            x: 0,
            y: 0,
        },
    ],
    items: [
        {
            name: 'Reel Frame',
            type: AssetType.SPRITE,
            visible: false,
            src: `reel_main.png`,
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container = data.component.componentElement as BaseContainer
                        applyReelFrameBaseLayout(container)
                        container.alpha = 0
                        container.visible = true
                        gsap.to(container, {
                            duration: 2,
                            alpha: 1,
                            onStart: () => {
                                //## If the hold and win is active, we want to change the texture to the hold and win texture
                                if (isHoldAndWinFrameActive(data)) {
                                    applyReelFrameTexture(container, 'reel_hold-win.png')
                                } else {
                                    applyReelFrameTexture(container, 'reel_main.png')
                                }

                                applyReelFrameBaseLayout(container)

                                // Frame is on screen — start flourish now (and every 10s via timer).
                                const reelFrameAnimation = container.parent?.getChildByName(
                                    'Reel Frame Animation'
                                ) as SpineAnimation
                                if (reelFrameAnimation && Boolean(data.signalData)) {
                                    reelFrameAnimation.visible = true
                                    reelFrameAnimation.alpha = 1
                                    startReelFrameAnimation(reelFrameAnimation)
                                }
                            },
                        })
                    },
                },

                {
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        gsap.killTweensOf(element)
                        gsap.to(element, {
                            duration: 0.35,
                            alpha: 0,
                            onComplete: () => {
                                //## If the hold and win is active, we want to change the texture to the hold and win texture
                                if (isHoldAndWinFrameActive(data)) {
                                    applyReelFrameTexture(element, 'reel_hold-win.png')
                                } else {
                                    applyReelFrameTexture(element, 'reel_main.png')
                                }
                            },
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        gsap.killTweensOf(element)
                        element.visible = true
                        element.alpha = 0
                        syncReelFrameTextureAndLayout(data)
                        gsap.to(element, {
                            duration: 0.25,
                            alpha: 1,
                        })
                    },
                },

                {
                    onGameSignalsTriggered: [GameSignals.bonusStart],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        // Keep frame on base coords in FS (matches reels). Re-pin after layout settle.
                        applyReelFrameBaseLayout(data.component.componentElement)
                        scheduleReelFrameLayoutSync(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).holdAndWinTransitionStart],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        applyReelFrameTexture(data.component.componentElement, 'reel_hold-win.png')
                        applyReelFrameBaseLayout(data.component.componentElement)
                        scheduleReelFrameLayoutSync(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).holdAndWinStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        applyReelFrameTexture(data.component.componentElement, 'reel_main.png')
                        applyReelFrameBaseLayout(data.component.componentElement)
                        scheduleReelFrameLayoutSync(data.component.componentElement)
                    },
                },
                {
                    // FS summary dismiss / base restore — re-pin frame to base layout (not FS snapshots).
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground, GameSignals.bonusStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        applyReelFrameTexture(data.component.componentElement, 'reel_main.png')
                        applyReelFrameBaseLayout(data.component.componentElement)
                        scheduleReelFrameLayoutSync(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        // gameResized runs before updateLayout; schedule so we win after orientation settle.
                        syncReelFrameTextureAndLayout(data, { schedule: true })
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 434.5,
                    y: -31,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: -39,
                    y: 527,
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.8,
                    y: 0.8,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.76,
                    y: 0.76,
                },
            ],
        },
        {
            name: 'Reel Frame Animation',
            type: AssetType.SPINE,
            visible: false,
            src: 'reelFrameAnimation',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: [REEL_FRAME_ANIMATION_MAIN],
                    loops: [false],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                //## When the splash screen is completed, we want to start the reel frame animation if the container is visible
                {
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const container: SpineAnimation = data.component.componentElement
                        const show = Boolean(data.signalData)
                        container.visible = show
                        container.alpha = show ? 1 : 0
                        if (show) {
                            startReelFrameAnimation(container)
                        }
                    },
                },

                //## Hide flourish as soon as FS/H&W intro starts (before popup display delay).
                // stopReelFrameAnimation clears the 10s timer so it cannot force alpha back to 1.
                {
                    onGameSignalsTriggered: [
                        GameSignals.showFreeSpinsIntro,
                        (GameSignals as any).featureIntroRequested,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element: SpineAnimation = data.component.componentElement
                        gsap.killTweensOf(element)
                        stopReelFrameAnimation(element)
                    },
                },

                //## When the popup is hidden, we want to show the reel frame animation
                {
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element: SpineAnimation = data.component.componentElement
                        gsap.killTweensOf(element)
                        element.visible = true
                        element.alpha = 0
                        gsap.to(element, {
                            duration: 0.25,
                            alpha: 1,
                            onComplete: () => {
                                startReelFrameAnimation(element)
                            },
                        })
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1047,
                    y: 394,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 543,
                    y: 932.5,
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.8,
                    y: 0.8,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.76,
                    y: 0.76,
                },
            ],
        },
    ],
}
